import React, { useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { getToken } from "./utils/auth";
import { Toaster } from "react-hot-toast";
import AdminGuard from "./components/Guards/AdminGuard";
import { ThemeProvider, useTheme } from "./context/ThemeContext";
import { MusicProvider } from "./context/MusicContext";
import { DataProvider, useData } from "./context/DataContext";
import Layout from "./components/Layout/Layout";
import SidebarLayout from "./components/Layout/SidebarLayout";
import BackgroundMusic from "./components/BackgroundMusic";

import HomePage from "./pages/HomePage";
import ClassesPage from "./pages/ClassesPage";
import CreateClassPage from "./pages/CreateClassPage";
import EditQuizPage from "./pages/EditQuizPage";
import EditClassPage from "./pages/EditClassPage";
import DocumentsPage from "./pages/DocumentsPage";
import DocumentViewerPage from "./pages/DocumentViewerPage";
import QuizPage from "./pages/QuizPage";
import ResultsPage from "./pages/ResultsPage";
import ClassViewPage from "./pages/ClassViewPage";
import ProfilePage from "./pages/ProfilePage";
import AdminPage from "./pages/AdminPage";
import MembersPage from "./pages/MembersPage";
import MaintenancePage from "./pages/MaintenancePage";
import GalleryPage from "./pages/GalleryPage";
import ForumPage from "./pages/ForumPage";
import ForumPostDetailPage from "./pages/ForumPostDetailPage";
import CreateForumPostPage from "./pages/CreateForumPostPage";
import EditForumPostPage from './pages/EditForumPostPage';
import ForumApprovalPage from './pages/admin/ForumApprovalPage';



import LiveStreamPage from './pages/LiveStreamPage';
import { IS_MAINTENANCE_MODE, canBypassMaintenance } from "./utils/maintenanceConfig";


// ThemedToaster component để đổi màu theo theme
function ThemedToaster() {
  const { isDarkMode } = useTheme();
  return (
    <Toaster
      position="bottom-center"
      reverseOrder={false}
      gutter={8}
      containerClassName=""
      containerStyle={{ bottom: "20px", zIndex: 100000 }}
      toastOptions={{
        className: "",
        duration: 4000,
        style: isDarkMode
          ? {
            background:
              "linear-gradient(135deg, rgba(45, 55, 72, 0.95), rgba(26, 32, 44, 0.95))",
            color: "#f7fafc",
            borderRadius: "10px",
            padding: "12px 16px",
            fontSize: "13px",
            fontWeight: "500",
            boxShadow: "0 8px 20px rgba(0, 0, 0, 0.35)",
            backdropFilter: "blur(10px)",
            border: "1px solid rgba(116, 129, 140, 0.3)",
            minWidth: "200px",
            maxWidth: "400px",
            whiteSpace: "nowrap" as const,
            overflow: "hidden",
            textOverflow: "ellipsis",
          }
          : {
            background: "linear-gradient(135deg, #fff, #f3f4f6)",
            color: "#222",
            borderRadius: "10px",
            padding: "12px 16px",
            fontSize: "13px",
            fontWeight: "500",
            boxShadow: "0 8px 20px rgba(0,0,0,0.08)",
            backdropFilter: "blur(10px)",
            border: "1px solid #e5e7eb",
            minWidth: "200px",
            maxWidth: "400px",
            whiteSpace: "nowrap" as const,
            overflow: "hidden",
            textOverflow: "ellipsis",
          },
        success: {
          duration: 3000,
          iconTheme: {
            primary: isDarkMode ? "#10b981" : "#059669",
            secondary: "#fff",
          },
          style: isDarkMode
            ? {
              background:
                "linear-gradient(135deg, rgba(16, 185, 129, 0.9), rgba(5, 150, 105, 0.9))",
              color: "#fff",
              borderRadius: "10px",
              padding: "12px 16px",
              fontSize: "13px",
              fontWeight: "500",
              boxShadow: "0 8px 20px rgba(16, 185, 129, 0.3)",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(16, 185, 129, 0.4)",
              minWidth: "200px",
              maxWidth: "400px",
              whiteSpace: "nowrap" as const,
              overflow: "hidden",
              textOverflow: "ellipsis",
            }
            : {
              background: "linear-gradient(135deg, #6ee7b7, #a7f3d0)",
              color: "#065f46",
              borderRadius: "10px",
              padding: "12px 16px",
              fontSize: "13px",
              fontWeight: "500",
              boxShadow: "0 8px 20px rgba(16, 185, 129, 0.08)",
              backdropFilter: "blur(10px)",
              border: "1px solid #6ee7b7",
              minWidth: "200px",
              maxWidth: "400px",
              whiteSpace: "nowrap" as const,
              overflow: "hidden",
              textOverflow: "ellipsis",
            },
        },
        error: {
          duration: 4000,
          iconTheme: {
            primary: isDarkMode ? "#f87171" : "#ef4444",
            secondary: "#fff",
          },
          style: isDarkMode
            ? {
              background:
                "linear-gradient(135deg, rgba(239, 68, 68, 0.9), rgba(220, 38, 38, 0.9))",
              color: "#fff",
              borderRadius: "10px",
              padding: "12px 16px",
              fontSize: "13px",
              fontWeight: "500",
              boxShadow: "0 8px 20px rgba(239, 68, 68, 0.3)",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(239, 68, 68, 0.4)",
              minWidth: "200px",
              maxWidth: "400px",
              whiteSpace: "nowrap" as const,
              overflow: "hidden",
              textOverflow: "ellipsis",
            }
            : {
              background: "linear-gradient(135deg, #fca5a5, #fecaca)",
              color: "#991b1b",
              borderRadius: "10px",
              padding: "12px 16px",
              fontSize: "13px",
              fontWeight: "500",
              boxShadow: "0 8px 20px rgba(239, 68, 68, 0.08)",
              backdropFilter: "blur(10px)",
              border: "1px solid #fca5a5",
              minWidth: "200px",
              maxWidth: "400px",
              whiteSpace: "nowrap" as const,
              overflow: "hidden",
              textOverflow: "ellipsis",
            },
        },
      }}
    />
  );
}

// Component để bảo vệ các route
const MaintenanceGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { hasEntered, maintenanceMode } = useData();
  const location = useLocation();

  // 1. Kiểm tra chế độ bảo trì
  if (maintenanceMode && !canBypassMaintenance()) {
    // Nếu đang ở trang Welcome hoặc các trang forgot password thì giữ nguyên
    if (location.pathname === "/welcome" ||
      location.pathname === "/forgot-password-contact"
      /* || location.pathname === "/forgot-password" ||
      location.pathname === "/reset-password" */) {
      return <>{children}</>;
    }
    // Nếu không thì redirect về maintenance
    return <Navigate to="/welcome" replace />;
  }

  // 2. Chế độ bình thường (Landing Mode)

  // Nếu đang ở trang Welcome -> Luôn cho phép
  if (location.pathname === "/welcome") {
    return <>{children}</>;
  }

  // Các route public khác (Forgot Password, Live Stream direct access) -> Cho phép truy cập
  if (/* location.pathname === '/forgot-password' || */
    location.pathname === '/forgot-password-contact' ||
    location.pathname.startsWith('/live')
    /* || location.pathname === '/reset-password' */) {
    return <>{children}</>;
  }

  // Các route protected (Home, Classes, etc.)
  // Nếu chưa "Enter" (chưa bấm Start từ Welcome Page) -> Redirect về maintenance
  if (!hasEntered) {
    return <Navigate to="/welcome" replace />;
  }

  // Nếu đã Enter -> Cho phép truy cập
  return <>{children}</>;
};

// Component to preload data on app initialization
function DataPreloader() {
  const { preloadEssentialData, isDataLoaded } = useData();
  const [hasTriggered, setHasTriggered] = React.useState(false);

  useEffect(() => {
    const token = getToken();

    // Only trigger once when token is available and not already loaded
    if (token && !hasTriggered && !isDataLoaded) {
      setHasTriggered(true);
      preloadEssentialData(token);
    }
  }, [preloadEssentialData, isDataLoaded, hasTriggered]);

  // Listen for login event (when user logs in)
  useEffect(() => {
    const handleLogin = (event: any) => {
      const token = event.detail?.token || getToken();
      if (token && !hasTriggered && !isDataLoaded) {
        setHasTriggered(true);
        preloadEssentialData(token);
      }
    };

    window.addEventListener('auth:login', handleLogin);
    return () => window.removeEventListener('auth:login', handleLogin);
  }, [preloadEssentialData, isDataLoaded, hasTriggered]);

  return null; // This component doesn't render anything
}

// Global Database Optimizer
function DatabaseOptimizer() {
  const [hasRun, setHasRun] = React.useState(false);

  useEffect(() => {
    const token = getToken();
    const alreadyRun = sessionStorage.getItem('db_optimized_global');

    if (token && !hasRun && !alreadyRun) {
      setHasRun(true);
      // Run in background
      import('./utils/api').then(({ getApiBaseUrl }) => {
        console.log('Running global database optimization...');
        fetch(`${getApiBaseUrl()}/admin/optimize-db`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        }).then(r => {
          if (r.ok) {
            console.log('Global database optimization complete.');
            sessionStorage.setItem('db_optimized_global', 'true');
          }
        }).catch(e => console.error('Global optimization failed', e));
      });
    }
  }, [hasRun]);

  return null;
}

function App() {
  // Offline signal is now handled by Firebase presence
  // useEffect(() => {
  //   const handleVisibilityChange = () => {
  //     if (document.visibilityState === 'hidden') {
  //       const token = getToken();
  //       if (navigator.sendBeacon && token) {
  //         const url = `${getApiBaseUrl()}/auth/offline-signal?token=${encodeURIComponent(token)}`;
  //         navigator.sendBeacon(url);
  //       }
  //     }
  //   };

  //   document.addEventListener('visibilitychange', handleVisibilityChange);

  //   return () => {
  //     document.removeEventListener('visibilitychange', handleVisibilityChange);
  //   };
  // }, []);

  // Log trạng thái bảo trì khi app khởi động (chỉ trong development)
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      // console.log('Checking maintenance status via API...');
    }
  }, []);

  return (
    <ThemeProvider>
      <MusicProvider>
        <DataProvider>
          <DataPreloader />
          <DatabaseOptimizer />
          <Router>
            <div className="h-screen flex flex-col overflow-hidden bg-gray-50 dark:bg-gray-900 transition-colors duration-200">
              <div className="flex-1 overflow-hidden relative w-full">
                <Routes>
                  {/* Trang bảo trì / Landing Page */}
                  <Route
                    path="/welcome"
                    element={
                      <MaintenanceGuard>
                        <MaintenancePage />
                      </MaintenanceGuard>
                    }
                  />

                  {/* Forgot Password Contact Page - REMOVED (Integrated into MaintenancePage) */}

                  {/* Tất cả các route khác đều được bảo vệ bởi MaintenanceGuard */}
                  <Route
                    path="/"
                    element={
                      <MaintenanceGuard>
                        <SidebarLayout noPadding={true}>
                          <HomePage />
                        </SidebarLayout>
                      </MaintenanceGuard>
                    }
                  />
                  <Route
                    path="/classes"
                    element={
                      <MaintenanceGuard>
                        <SidebarLayout noPadding={true}>
                          <ClassesPage />
                        </SidebarLayout>
                      </MaintenanceGuard>
                    }
                  />
                  <Route
                    path="/profile"
                    element={
                      <MaintenanceGuard>
                        <SidebarLayout noPadding={true}>
                          <ProfilePage />
                        </SidebarLayout>
                      </MaintenanceGuard>
                    }
                  />
                  <Route
                    path="/admin"
                    element={
                      <MaintenanceGuard>
                        <AdminGuard>
                          <SidebarLayout noPadding={true}>
                            <AdminPage />
                          </SidebarLayout>
                        </AdminGuard>
                      </MaintenanceGuard>
                    }
                  />
                  <Route
                    path="/create"
                    element={
                      <MaintenanceGuard>
                        <SidebarLayout noPadding={true}>
                          <CreateClassPage />
                        </SidebarLayout>
                      </MaintenanceGuard>
                    }
                  />
                  <Route
                    path="/edit-quiz"
                    element={
                      <MaintenanceGuard>
                        <Layout>
                          <EditQuizPage />
                        </Layout>
                      </MaintenanceGuard>
                    }
                  />
                  <Route
                    path="/documents"
                    element={
                      <MaintenanceGuard>
                        <SidebarLayout noPadding={true}>
                          <DocumentsPage />
                        </SidebarLayout>
                      </MaintenanceGuard>
                    }
                  />
                  <Route
                    path="/document/:id"
                    element={
                      <MaintenanceGuard>
                        <SidebarLayout noPadding={true}>
                          <DocumentViewerPage />
                        </SidebarLayout>
                      </MaintenanceGuard>
                    }
                  />
                  <Route
                    path="/gallery"
                    element={
                      <MaintenanceGuard>
                        <SidebarLayout noPadding={true}>
                          <GalleryPage />
                        </SidebarLayout>
                      </MaintenanceGuard>
                    }
                  />
                  <Route
                    path="/forum"
                    element={
                      <MaintenanceGuard>
                        <SidebarLayout noPadding={true}>
                          <ForumPage />
                        </SidebarLayout>
                      </MaintenanceGuard>
                    }
                  />
                  <Route
                    path="/members"
                    element={
                      <MaintenanceGuard>
                        <SidebarLayout noPadding={true}>
                          <MembersPage />
                        </SidebarLayout>
                      </MaintenanceGuard>
                    }
                  />
                  <Route
                    path="/forum/approval"
                    element={
                      <MaintenanceGuard>
                        <AdminGuard>
                          <SidebarLayout noPadding={true}>
                            <ForumApprovalPage />
                          </SidebarLayout>
                        </AdminGuard>
                      </MaintenanceGuard>
                    }
                  />
                  <Route
                    path="/forum/create"
                    element={
                      <MaintenanceGuard>
                        <SidebarLayout noPadding={true}>
                          <CreateForumPostPage />
                        </SidebarLayout>
                      </MaintenanceGuard>
                    }
                  />
                  <Route
                    path="/forum/edit/:id"
                    element={
                      <MaintenanceGuard>
                        <SidebarLayout noPadding={true}>
                          <EditForumPostPage />
                        </SidebarLayout>
                      </MaintenanceGuard>
                    }
                  />
                  <Route
                    path="/forum/:id"
                    element={
                      <MaintenanceGuard>
                        <SidebarLayout noPadding={true}>
                          <ForumPostDetailPage />
                        </SidebarLayout>
                      </MaintenanceGuard>
                    }
                  />

                  <Route
                    path="/live"
                    element={
                      <MaintenanceGuard>
                        <LiveStreamPage />
                      </MaintenanceGuard>
                    }
                  />
                  <Route
                    path="/live/:id"
                    element={
                      <MaintenanceGuard>
                        <LiveStreamPage />
                      </MaintenanceGuard>
                    }
                  />
                  <Route
                    path="/quiz/:quizId"
                    element={
                      <MaintenanceGuard>
                        <SidebarLayout noPadding={true}>
                          <QuizPage />
                        </SidebarLayout>
                      </MaintenanceGuard>
                    }
                  />
                  <Route
                    path="/results/:quizId"
                    element={
                      <MaintenanceGuard>
                        <SidebarLayout noPadding={true}>
                          <ResultsPage />
                        </SidebarLayout>
                      </MaintenanceGuard>
                    }
                  />
                  <Route
                    path="/class/:classId"
                    element={
                      <MaintenanceGuard>
                        <SidebarLayout noPadding={true}>
                          <ClassViewPage />
                        </SidebarLayout>
                      </MaintenanceGuard>
                    }
                  />

                  {/* Route với FixedLayout -> Chuyển sang SidebarLayout */}
                  <Route
                    path="/edit-class/:classId"
                    element={
                      <MaintenanceGuard>
                        <SidebarLayout>
                          <EditClassPage />
                        </SidebarLayout>
                      </MaintenanceGuard>
                    }
                  />
                </Routes>

              </div>
            </div>
            {/* Music Player & Toaster */}
            <BackgroundMusic />
            <ThemedToaster />
          </Router>
        </DataProvider>
      </MusicProvider>
    </ThemeProvider>
  );
}

// Helper component để check location cho Music Player
// const RenderMusicPlayer = () => {
//   const location = useLocation();
//   // Chỉ hiện nhạc khi không phải mode bảo trì VÀ không ở trang /welcome
//   if (IS_MAINTENANCE_MODE || location.pathname === '/welcome') {
//     return null;
//   }
//   return <BackgroundMusic />;
// };

export default App;
